'use client';

import { FC, Fragment, useState, useCallback, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { cn } from '@/lib/utils';
import { RevealOnScroll } from '@/components/common/RevealOnScroll';
import DynamicMarquee from '@/components/DynamicMarquee';
import { useIsMobile } from '@/hooks';

export type IndustryTestimonialsCarouselProps =
  SliceComponentProps<Content.IndustryTestimonialsCarouselSlice>;

const IndustryTestimonialsCarousel: FC<IndustryTestimonialsCarouselProps> = ({
  slice,
}) => {
  const { case_studies, background_image } = slice.primary;
  const isStandAlone = slice.variation === 'standAlone';
  // M-35: render ONE case-study cards instance (desktop OR mobile) instead of
  // both in the DOM, so the cards aren't duplicated in crawlable/visible HTML.
  // Aligned to the `lg` (1024px) breakpoint the layouts use.
  const isMobileCards = useIsMobile({ width: 1023 });
  const isWithCertifications = slice.variation === 'withCertifications';
  const eyebrow = !isStandAlone ? slice.primary.eyebrow : undefined;
  const title = !isStandAlone ? slice.primary.title : undefined;
  const intro = !isStandAlone ? slice.primary.intro : undefined;
  const cta =
    slice.variation === 'with_case_study_slider' ? slice.primary.cta : null;
  const primaryCta =
    slice.variation === 'with_case_study_slider'
      ? (
          slice.primary as {
            primary_cta?: import('@prismicio/client').LinkField;
          }
        ).primary_cta
      : null;
  const introEmphasis =
    slice.variation === 'with_case_study_slider'
      ? (slice.primary as { intro_emphasis?: string }).intro_emphasis
      : null;
  const industriesTitle =
    slice.variation === 'with_case_study_slider'
      ? (slice.primary as { industries_title?: string }).industries_title
      : null;
  const industries =
    slice.variation === 'with_case_study_slider'
      ? ((
          slice.primary as {
            industries?: { name?: string; subtitle?: string }[];
          }
        ).industries ?? [])
      : [];
  const certifications =
    slice.variation === 'withCertifications'
      ? slice.primary.certifications
      : [];
  const certHighlight =
    slice.variation === 'withCertifications'
      ? (slice.primary as { highlight?: string }).highlight
      : null;
  const certRecognitionLogo =
    slice.variation === 'withCertifications'
      ? (
          slice.primary as {
            recognition_logo?: import('@prismicio/client').ImageField;
          }
        ).recognition_logo
      : null;
  const certPostLogoDescription =
    slice.variation === 'withCertifications'
      ? (
          slice.primary as {
            post_logo_description?: import('@prismicio/client').RichTextField;
          }
        ).post_logo_description
      : null;
  const certRecognitionCta =
    slice.variation === 'withCertifications'
      ? (
          slice.primary as {
            recognition_cta?: import('@prismicio/client').LinkField;
          }
        ).recognition_cta
      : null;
  const certRecognitionCards =
    slice.variation === 'withCertifications'
      ? ((
          slice.primary as {
            recognition_cards?: {
              card_tagline?: string;
              card_title?: import('@prismicio/client').RichTextField;
              card_description?: import('@prismicio/client').RichTextField;
              cta_label?: string;
              cta_link?: import('@prismicio/client').LinkField;
            }[];
          }
        ).recognition_cards ?? [])
      : [];
  // M-12: elevated tier of certifications (ISO 27001, Google Cloud, …) shown
  // big + in color with a label, above the de-emphasized directory badges.
  const primaryCertifications =
    slice.variation === 'withCertifications'
      ? ((
          slice.primary as {
            primary_certifications?: {
              logo?: import('@prismicio/client').ImageField;
              description?: string;
            }[];
          }
        ).primary_certifications ?? [])
      : [];

  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % case_studies.length);
  }, [case_studies.length]);

  const goPrev = useCallback(() => {
    setActiveIndex(
      (prev) => (prev - 1 + case_studies.length) % case_studies.length,
    );
  }, [case_studies.length]);

  // Fade-in on scroll
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReduced) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' },
    );

    [
      { ref: headerRef, delay: '0ms' },
      { ref: cardsRef, delay: isStandAlone ? '0ms' : '300ms' },
    ].forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, [isStandAlone]);

  // An empty `withCertifications` slice (no header, certs, cards or case
  // studies) should render nothing instead of an empty padded band.
  if (isWithCertifications) {
    const hasCertContent =
      !!eyebrow ||
      isFilled.richText(title) ||
      isFilled.richText(intro) ||
      !!certHighlight ||
      isFilled.image(certRecognitionLogo) ||
      isFilled.richText(certPostLogoDescription) ||
      isFilled.link(certRecognitionCta) ||
      certRecognitionCards.length > 0 ||
      primaryCertifications.some((item) => isFilled.image(item.logo)) ||
      certifications.length > 0 ||
      case_studies.length > 0;
    if (!hasCertContent) {
      return null;
    }
  }

  return (
    <section
      ref={sectionRef}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={cn(
        'relative overflow-hidden px-4 py-[60px] md:py-[80px]',
        isStandAlone
          ? 'bg-white md:px-[30px]'
          : 'mx-[30px] rounded-[24px] md:px-[60px]',
        isWithCertifications && 'bg-white',
      )}
    >
      {/* Section background image */}
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          className="absolute inset-0 size-full object-cover"
          fallbackAlt=""
        />
      )}
      {!isWithCertifications && isFilled.image(background_image) && (
        <div className="absolute inset-0 bg-[rgba(4,17,24,0.7)]" />
      )}

      {/* Header */}
      {!isStandAlone && (
        <div
          ref={headerRef}
          className="relative z-[1] flex flex-col items-center gap-[24px] px-4 pb-[32px] md:px-[90px]"
        >
          {eyebrow && (
            <span
              className={cn(
                'inline-flex border px-[16px] py-[8px] text-center text-[14px] font-normal uppercase leading-[1.2]',
                isWithCertifications
                  ? 'rounded-[89px] border-[#4B0FEA] text-[#041118]'
                  : 'rounded-full border-[#00f557] text-white',
              )}
            >
              {eyebrow}
            </span>
          )}

          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2
                    className={cn(
                      'w-full break-words text-center text-[32px] font-semibold leading-[1.2] md:text-[42px]',
                      isWithCertifications ? 'text-[#041118]' : 'text-white',
                    )}
                  >
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {isFilled.richText(intro) && (
            <PrismicRichText
              field={intro}
              components={{
                paragraph: ({ children }) => (
                  <p
                    className={cn(
                      'w-full max-w-[1080px] text-center text-[16px] font-light leading-[1.5]',
                      isWithCertifications ? 'text-[#041118]' : 'text-white',
                    )}
                  >
                    {children}
                  </p>
                ),
              }}
            />
          )}

          {introEmphasis && (
            <p className="max-w-[1080px] text-center text-[16px] font-medium leading-[1.5] text-[#00f557]">
              {introEmphasis}
            </p>
          )}

          {isWithCertifications && certHighlight && (
            <p className="w-full max-w-[1080px] text-balance text-center text-[16px] font-semibold leading-[1.4] text-[#4B0FEA] md:text-[18px]">
              {certHighlight}
            </p>
          )}

          {isWithCertifications && isFilled.image(certRecognitionLogo) && (
            <div className="w-full max-w-[480px]">
              <PrismicNextImage
                field={certRecognitionLogo}
                className="h-auto w-full object-contain"
                fallbackAlt=""
              />
            </div>
          )}

          {isWithCertifications &&
            isFilled.richText(certPostLogoDescription) && (
              <div className="w-full max-w-[820px] break-words text-center font-inter text-[18px] font-bold leading-[1.4] text-[#041118] md:text-[24px] [&_p]:m-0 [&_p+p]:mt-2">
                <PrismicRichText field={certPostLogoDescription} />
              </div>
            )}

          {isWithCertifications && isFilled.link(certRecognitionCta) && (
            <div className="pt-[8px]">
              <PrismicNextLink
                field={certRecognitionCta}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-[#041118]/30 bg-transparent px-[28px] py-[14px] text-[16px] font-bold leading-[1.5] text-[#041118] transition-colors hover:bg-[#041118] hover:text-white"
              />
            </div>
          )}

          {(isFilled.link(primaryCta) || isFilled.link(cta)) && (
            <div className="flex flex-col items-stretch justify-center gap-[16px] pt-[8px] sm:flex-row sm:items-center">
              {isFilled.link(primaryCta) && (
                <PrismicNextLink
                  field={primaryCta}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#00f557] px-[28px] py-[14px] text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90"
                />
              )}
              {isFilled.link(cta) && (
                <PrismicNextLink
                  field={cta}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-white bg-transparent px-[28px] py-[14px] text-[16px] font-bold leading-[1.5] text-white transition-colors hover:bg-white hover:text-[#041118]"
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Recognition Cards (withCertifications only) ── */}
      {isWithCertifications && certRecognitionCards.length > 0 && (
        <div className="relative z-[1] mx-auto mt-[16px] grid w-full max-w-[1080px] grid-cols-1 gap-6 md:mt-[32px] md:grid-cols-2 md:gap-8">
          {certRecognitionCards.slice(0, 2).map((card, i) => (
            <div
              key={i}
              className="flex flex-col gap-5 rounded-[24px] border border-[#E5E5E5] bg-white px-7 py-8 md:px-9 md:py-9"
            >
              {card.card_tagline && (
                <div className="flex items-center gap-2 text-[12px] font-semibold uppercase leading-[1.3] tracking-[0.04em] text-[#4B0FEA] md:text-[13px]">
                  <span className="inline-block size-[8px] rounded-full border-[1.5px] border-[#4B0FEA]" />
                  {card.card_tagline}
                </div>
              )}

              {isFilled.richText(card.card_title) && (
                <div className="text-[#041118] [&_h3]:m-0 [&_h3]:text-[20px] md:[&_h3]:text-[22px] [&_h3]:font-semibold [&_h3]:leading-[1.3]">
                  <PrismicRichText field={card.card_title} />
                </div>
              )}

              {isFilled.richText(card.card_description) && (
                <div className="text-[14px] font-normal leading-[1.55] text-[#041118]/70 md:text-[15px] [&_p]:m-0 [&_p+p]:mt-2">
                  <PrismicRichText field={card.card_description} />
                </div>
              )}

              {(card.cta_label || isFilled.link(card.cta_link)) && (
                <div className="mt-2">
                  {isFilled.link(card.cta_link) ? (
                    <PrismicNextLink
                      field={card.cta_link}
                      className="inline-flex items-center gap-2 rounded-full border border-[#041118]/80 bg-transparent px-5 py-2.5 text-[14px] font-semibold leading-[1.4] text-[#041118] transition-colors hover:bg-[#041118] hover:text-white"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        className="h-4 w-4"
                      >
                        <path
                          d="M3 8H13M13 8L8.5 3.5M13 8L8.5 12.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{card.cta_label || 'Read more'}</span>
                    </PrismicNextLink>
                  ) : (
                    <span className="inline-flex items-center gap-2 rounded-full border border-[#041118]/80 bg-transparent px-5 py-2.5 text-[14px] font-semibold leading-[1.4] text-[#041118]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        className="h-4 w-4"
                      >
                        <path
                          d="M3 8H13M13 8L8.5 3.5M13 8L8.5 12.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{card.cta_label}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Desktop Cards Row ── */}
      {!isMobileCards && (
        <div ref={cardsRef} className="relative z-[1] hidden gap-[8px] lg:flex">
          {case_studies.map((item, index) => {
            const isActive = index === activeIndex;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const cardBg = (item as any).background_image;

            return (
              <div
                key={index}
                role="button"
                tabIndex={0}
                aria-expanded={isActive}
                aria-label={item.industry_label ?? `Card ${index + 1}`}
                onClick={() => !isActive && setActiveIndex(index)}
                onKeyDown={(e) => {
                  if (!isActive && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setActiveIndex(index);
                  }
                }}
                className={cn(
                  'relative h-[550px] overflow-hidden rounded-[100px] transition-all duration-700 ease-in-out',
                  isActive
                    ? 'flex-[1_1_0%] min-w-0'
                    : 'flex-[0_0_136px] cursor-pointer hover:flex-[0_0_142px]',
                )}
              >
                {/* Card background image */}
                {isFilled.image(cardBg) && (
                  <PrismicNextImage
                    field={cardBg}
                    className="absolute inset-0 size-full rounded-[100px] object-cover"
                    fallbackAlt=""
                  />
                )}

                {/* Blur overlay — visible when collapsed, transparent when active */}
                <div
                  className={cn(
                    'absolute inset-0 rounded-[100px] transition-all duration-700',
                    isActive
                      ? 'bg-transparent backdrop-blur-0'
                      : 'bg-[rgba(5,29,25,0.7)] backdrop-blur-[21px]',
                  )}
                />

                {/* ── Expanded content (right panel) ── */}
                <div
                  className={cn(
                    'absolute inset-y-0 right-0 flex flex-col justify-between overflow-hidden rounded-r-[100px] bg-black/60 backdrop-blur-[7.5px] transition-all duration-700',
                    isActive
                      ? 'w-[581px] px-[100px] py-[60px] opacity-100'
                      : 'w-0 px-0 py-[60px] opacity-0 pointer-events-none',
                  )}
                >
                  {/* Top: industry label */}
                  <div className="flex shrink-0 items-center gap-[24px]">
                    <span className="whitespace-nowrap text-[18px] font-normal uppercase leading-[1.2] text-white">
                      {item.industry_label}
                    </span>
                    <span className="size-[12px] shrink-0 rounded-full border-2 border-white" />
                  </div>

                  {/* Bottom: chip + logo + headline + description + emphasis + cta + nav */}
                  <div className="flex flex-col gap-[24px]">
                    {(item as { chip_label?: string }).chip_label && (
                      <span className="inline-flex w-fit rounded-full border border-[#00f557] px-[16px] py-[6px] text-[12px] font-normal uppercase leading-[1.2] tracking-[0.04em] text-white">
                        {(item as { chip_label?: string }).chip_label}
                      </span>
                    )}

                    {isFilled.image(item.logo) && (
                      <PrismicNextImage
                        field={item.logo}
                        className="h-[38px] w-auto self-start object-contain"
                        fallbackAlt=""
                      />
                    )}

                    {isFilled.richText(item.headline) && (
                      <PrismicRichText
                        field={item.headline}
                        components={{
                          heading3: ({ children }) => (
                            <p className="text-[28px] font-bold leading-[1.2] text-white">
                              {children}
                            </p>
                          ),
                        }}
                      />
                    )}

                    {isFilled.richText(
                      (
                        item as {
                          description?: import('@prismicio/client').RichTextField;
                        }
                      ).description,
                    ) && (
                      <PrismicRichText
                        field={
                          (
                            item as {
                              description?: import('@prismicio/client').RichTextField;
                            }
                          ).description
                        }
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-[15px] font-light leading-[1.5] text-white/85">
                              {children}
                            </p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold">
                              {children}
                            </strong>
                          ),
                        }}
                      />
                    )}

                    {(item as { emphasis_text?: string }).emphasis_text && (
                      <p className="text-[15px] font-medium leading-[1.5] text-[#00f557]">
                        {(item as { emphasis_text?: string }).emphasis_text}
                      </p>
                    )}

                    <div className="flex items-center gap-[24px]">
                      {isFilled.link(item.case_study_link) && (
                        <PrismicNextLink
                          field={item.case_study_link}
                          className="shrink-0 rounded-full bg-[#00f557] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90"
                        />
                      )}

                      {/* Progress line */}
                      <div className="flex flex-1 items-center">
                        <div className="h-px w-[36px] bg-white" />
                        <div className="h-px flex-1 bg-white/30" />
                      </div>

                      {/* Prev / Next */}
                      <div className="flex items-center gap-[8px]">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            goPrev();
                          }}
                          className="flex size-[36px] items-center justify-center text-white transition-opacity hover:opacity-70"
                          aria-label="Previous"
                        >
                          <svg
                            width="36"
                            height="36"
                            viewBox="0 0 36 36"
                            fill="none"
                          >
                            <path
                              d="M22 8L12 18L22 28"
                              stroke="white"
                              strokeWidth="1"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            goNext();
                          }}
                          className="flex size-[36px] items-center justify-center text-white transition-opacity hover:opacity-70"
                          aria-label="Next"
                        >
                          <svg
                            width="36"
                            height="36"
                            viewBox="0 0 36 36"
                            fill="none"
                          >
                            <path
                              d="M14 8L24 18L14 28"
                              stroke="white"
                              strokeWidth="1"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Collapsed content (vertical label + expand icon) ── */}
                <div
                  className={cn(
                    'absolute inset-0 flex flex-col items-center justify-between py-[60px] transition-opacity duration-500',
                    isActive ? 'pointer-events-none opacity-0' : 'opacity-100',
                  )}
                >
                  {/* Rotated industry label */}
                  <div
                    className="flex flex-1 items-center justify-center"
                    style={{ containerType: 'size' }}
                  >
                    <div className="-rotate-90 flex w-[100cqh] flex-none items-center justify-end gap-[24px] whitespace-nowrap pr-[5px]">
                      <span className="text-[18px] font-normal uppercase leading-[1.2] text-white">
                        {item.industry_label}
                      </span>
                      <span className="size-[12px] shrink-0 rounded-full border-2 border-white" />
                    </div>
                  </div>

                  {/* Expand indicator */}
                  <button
                    className="flex size-[36px] items-center justify-center rounded-full border border-white/50 transition-colors hover:bg-white/10"
                    aria-label={`Expand ${item.industry_label}`}
                    tabIndex={-1}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M10 4V16M4 10H16"
                        stroke="white"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Mobile Cards (always expanded — no accordion) ── */}
      {isMobileCards && (
        <div className="relative z-[1] flex flex-col gap-[16px] lg:hidden">
          {case_studies.map((item, index) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const cardBg = (item as any).background_image;
            const description = (
              item as {
                description?: import('@prismicio/client').RichTextField;
              }
            ).description;
            const emphasisText = (item as { emphasis_text?: string })
              .emphasis_text;
            const chipLabel = (item as { chip_label?: string }).chip_label;

            return (
              <div
                key={index}
                className="relative min-h-[400px] overflow-hidden rounded-[24px]"
              >
                {isFilled.image(cardBg) && (
                  <PrismicNextImage
                    field={cardBg}
                    className="absolute inset-0 size-full object-cover"
                    fallbackAlt=""
                  />
                )}
                <div className="absolute inset-0 bg-black/60 backdrop-blur-[7.5px]" />

                <div className="relative flex min-h-[400px] flex-col justify-between gap-8 p-[24px]">
                  <div className="flex items-center gap-[12px]">
                    <span className="text-[16px] font-normal uppercase leading-[1.2] text-white">
                      {item.industry_label}
                    </span>
                    <span className="size-[10px] shrink-0 rounded-full border-2 border-white" />
                  </div>

                  <div className="flex flex-col gap-[16px]">
                    {chipLabel && (
                      <span className="inline-flex w-fit rounded-full border border-[#00f557] px-[14px] py-[5px] text-[11px] font-normal uppercase leading-[1.2] tracking-[0.04em] text-white">
                        {chipLabel}
                      </span>
                    )}
                    {isFilled.image(item.logo) && (
                      <PrismicNextImage
                        field={item.logo}
                        className="h-[28px] w-auto self-start object-contain"
                        fallbackAlt=""
                      />
                    )}
                    {isFilled.richText(item.headline) && (
                      <PrismicRichText
                        field={item.headline}
                        components={{
                          heading3: ({ children }) => (
                            <p className="text-[20px] font-semibold leading-[1.2] text-white">
                              {children}
                            </p>
                          ),
                        }}
                      />
                    )}
                    {isFilled.richText(description) && (
                      <PrismicRichText
                        field={description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-[14px] font-light leading-[1.5] text-white/85">
                              {children}
                            </p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold">
                              {children}
                            </strong>
                          ),
                        }}
                      />
                    )}
                    {emphasisText && (
                      <p className="text-[14px] font-medium leading-[1.5] text-[#00f557]">
                        {emphasisText}
                      </p>
                    )}
                    {isFilled.link(item.case_study_link) && (
                      <PrismicNextLink
                        field={item.case_study_link}
                        className="mt-1 w-fit rounded-full bg-[#00f557] px-[20px] py-[10px] text-[14px] font-bold leading-[1.5] text-[#002726]"
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Primary certifications: elevated tier (M-12) ── */}
      {primaryCertifications.some((item) => isFilled.image(item.logo)) && (
        <RevealOnScroll className="relative z-[1] mt-[32px] flex flex-wrap items-stretch justify-center gap-x-6 gap-y-6 md:mt-[40px] md:gap-x-10">
          {primaryCertifications
            .filter((item) => isFilled.image(item.logo))
            .map((item, index) => (
              <Fragment key={index}>
                {index > 0 && (
                  <span
                    aria-hidden="true"
                    className="hidden w-px self-stretch bg-[#E5E5E5] md:block"
                  />
                )}
                <div className="flex max-w-[260px] flex-col items-center justify-center gap-3 px-2 text-center">
                  <PrismicNextImage
                    field={item.logo}
                    className="w-auto object-contain"
                    style={{ height: 'clamp(40px, 4vw, 56px)' }}
                    fallbackAlt=""
                  />
                  {item.description && (
                    <p className="text-[14px] leading-[1.4] text-[#041118]/70 md:text-[15px]">
                      {item.description}
                    </p>
                  )}
                </div>
              </Fragment>
            ))}
        </RevealOnScroll>
      )}

      {/* ── Certifications Row (withCertifications variation) ── */}
      {certifications.length > 0 &&
        (() => {
          const filledCerts = certifications.filter((item) =>
            isFilled.image(item.certification),
          );
          if (filledCerts.length === 0) {
            return null;
          }
          return (
            <RevealOnScroll className="relative z-[1] mt-[48px] -mx-4 overflow-hidden border-y border-[#FAFAFA] py-[30px] md:-mx-[60px]">
              <DynamicMarquee
                speed={45}
                pauseOnHover
                autoFill
                gradient
                gradientColor="#FFFFFF"
                gradientWidth={80}
              >
                {filledCerts.map((item, index) => {
                  // Accept whatever shape Prismic returns for the color
                  // asset — Link-to-Media or Image — and pull a usable URL.
                  const colorRaw = (item as { certification_color?: unknown })
                    .certification_color;
                  const colorUrl =
                    colorRaw &&
                    typeof colorRaw === 'object' &&
                    'url' in colorRaw &&
                    typeof (colorRaw as { url?: unknown }).url === 'string' &&
                    (colorRaw as { url: string }).url.length > 0
                      ? (colorRaw as { url: string }).url
                      : undefined;
                  const logoStyle = {
                    /* Logo height scales smoothly: 36px min → 64px max */
                    height: 'clamp(36px, 3.5vw, 64px)',
                  } as const;
                  return (
                    <div
                      key={index}
                      className="group/cert relative mx-[clamp(20px,2.5vw,48px)] flex items-center justify-center"
                    >
                      {/* Default (grayscale) image */}
                      <PrismicNextImage
                        field={item.certification}
                        className={`w-auto max-w-none shrink-0 object-contain grayscale opacity-70 transition-all duration-300 ${
                          colorUrl
                            ? 'group-hover/cert:opacity-0'
                            : 'group-hover/cert:opacity-100 group-hover/cert:grayscale-0'
                        }`}
                        style={logoStyle}
                        fallbackAlt=""
                      />
                      {/* Color asset revealed on hover (only if uploaded).
                          Rendered as a CSS background so SVGs from Prismic
                          render reliably (Next/Image blocks unsized SVGs). */}
                      {colorUrl && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 bg-contain bg-center bg-no-repeat opacity-0 transition-opacity duration-300 group-hover/cert:opacity-100"
                          style={{ backgroundImage: `url("${colorUrl}")` }}
                        />
                      )}
                    </div>
                  );
                })}
              </DynamicMarquee>
            </RevealOnScroll>
          );
        })()}

      {/* ── Industries We Serve (with_case_study_slider variation) ── */}
      {(industriesTitle || industries.length > 0) && (
        <RevealOnScroll className="relative z-[1] mx-auto mt-[24px] flex w-full max-w-[1080px] flex-col items-center gap-[28px] md:mt-[36px]">
          {industriesTitle && (
            <p className="text-center text-[16px] font-bold leading-[1.4] text-white md:text-[18px]">
              {industriesTitle}
            </p>
          )}
          {industries.length > 0 && (
            <div className="relative w-full">
              <div
                className={cn(
                  'grid w-full gap-8 md:items-start md:gap-0',
                  industries.length === 4 && 'grid-cols-2 md:grid-cols-4',
                  industries.length === 3 && 'grid-cols-1 sm:grid-cols-3',
                  industries.length === 2 && 'grid-cols-2',
                  industries.length === 1 && 'grid-cols-1',
                  industries.length >= 5 &&
                    'grid-cols-2 sm:grid-cols-3 md:grid-cols-5',
                )}
              >
                {industries.map((industry, i) => (
                  <div
                    key={`industry-${i}`}
                    className="flex flex-col items-center gap-[4px] px-3 text-center md:px-4"
                  >
                    {industry.name && (
                      <p className="text-[18px] font-bold leading-[1.3] text-white md:text-[20px]">
                        {industry.name}
                      </p>
                    )}
                    {industry.subtitle && (
                      <p className="text-[13px] font-normal leading-[1.5] text-white/75 md:text-[14px]">
                        {industry.subtitle}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Purple vertical dividers between industries — solid color
                  that extends ~16px past the text on top and bottom so the
                  line reads taller than the content. */}
              {Array.from(
                { length: Math.max(industries.length - 1, 0) },
                (_, i) => (
                  <div
                    key={`industry-v-${i}`}
                    className="pointer-events-none absolute -top-4 -bottom-4 hidden w-px bg-[#a887ff] md:block"
                    style={{
                      left: `calc(100% / ${industries.length} * ${i + 1})`,
                    }}
                  />
                ),
              )}
            </div>
          )}
        </RevealOnScroll>
      )}
    </section>
  );
};

export default IndustryTestimonialsCarousel;
